import * as React from "react"
import { Search, Loader2, ImageOff } from "lucide-react"
import giphyLogo from "@/public/giphy.png"

interface GifResult {
  id: string
  title: string
  previewUrl: string
  fullUrl: string
  width: number
  height: number
}

interface GifPickerProps {
  onSelect: (gifUrl: string) => void
  onClose: () => void
}

export function GifPickerContent({ onSelect, onClose }: GifPickerProps) {
  const [search, setSearch] = React.useState("")
  const [debouncedSearch, setDebouncedSearch] = React.useState("")
  const [gifs, setGifs] = React.useState<GifResult[]>([])
  const [isLoading, setIsLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  // Debounce search input
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search)
    }, 400)
    return () => clearTimeout(timer)
  }, [search])

  // Fetch GIFs from backend proxy
  React.useEffect(() => {
    let cancelled = false
    setIsLoading(true)
    setError(null)

    const endpoint = debouncedSearch.trim()
      ? `/api/gifs/search?q=${encodeURIComponent(debouncedSearch.trim())}&limit=30`
      : `/api/gifs/trending?limit=30`

    fetch(endpoint)
      .then((res) => res.json())
      .then((data) => {
        if (cancelled) return
        if (data.success && data.gifs) {
          setGifs(data.gifs)
        } else {
          setError(data.message || "Failed to load GIFs")
        }
        setIsLoading(false)
      })
      .catch((err) => {
        if (cancelled) return
        console.error("GIF fetch error:", err)
        setError("Failed to load GIFs")
        setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [debouncedSearch])

  const handleSelect = (gif: GifResult) => {
    onSelect(gif.fullUrl)
  }

  return (
    <div
      className="w-[320px] md:w-[400px] bg-[#1C1C32] border-3 border-[#2A2A45] rounded-2xl shadow-[6px_6px_0_0_#1a1a1a] overflow-hidden"
      onClick={(e) => e.stopPropagation()}
    >
      {/* Search */}
      <div className="p-2.5 border-b-2 border-[#2A2A45]/40">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6A6A8A]" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Giphy"
            className="w-full pl-8 pr-3 py-2 text-sm font-medium rounded-xl border-2 border-[#2A2A45]/50 bg-[#1A1A30] focus:outline-none focus:ring-2 focus:ring-[#F37020] focus:border-transparent"
            autoFocus
          />
        </div>
      </div>

      {/* GIF grid */}
      <div className="h-[300px] overflow-y-auto p-2">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-[#6A6A8A]" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full gap-2 text-[#6A6A8A]">
            <ImageOff className="w-8 h-8" />
            <p className="text-sm font-medium">{error}</p>
            <p className="text-xs">Make sure GIPHY_API_KEY is set</p>
          </div>
        ) : gifs.length === 0 ? (
          <div className="flex items-center justify-center h-full text-sm text-[#6A6A8A] font-medium">
            No GIFs found
          </div>
        ) : (
          <div className="columns-2 gap-1.5 space-y-1.5">
            {gifs.map((gif) => (
              <button
                key={gif.id}
                onClick={() => handleSelect(gif)}
                className="w-full block rounded-lg overflow-hidden border-2 border-transparent hover:border-[#F37020] transition-colors break-inside-avoid"
              >
                <img
                  src={gif.previewUrl}
                  alt={gif.title}
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Powered by GIPHY attribution */}
      <div className="px-2.5 py-2 border-t-2 border-[#2A2A45]/40 flex items-center justify-end">
        <a
          href="https://giphy.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5"
        >
          <img src={giphyLogo} alt="Powered by GIPHY" className="h-3.5" />
        </a>
      </div>
    </div>
  )
}
